import type { ColumnDef } from '@tanstack/react-table'
import { CaretUp, CaretDown } from '@phosphor-icons/react'
import { Badge } from '@/components/ui/badge'
import type { OrgUser } from '@/api/users'

function SortableHeader({
  label,
  column,
}: {
  label: string
  column: { getIsSorted: () => false | 'asc' | 'desc'; toggleSorting: (desc?: boolean) => void }
}) {
  const sorted = column.getIsSorted()
  return (
    <button
      type="button"
      className="flex items-center gap-1 hover:text-foreground transition-colors -ml-2 px-2 py-1 rounded"
      onClick={() => column.toggleSorting(sorted === 'asc')}
    >
      {label}
      {sorted === 'asc' && <CaretUp className="h-3.5 w-3.5" weight="bold" />}
      {sorted === 'desc' && <CaretDown className="h-3.5 w-3.5" weight="bold" />}
    </button>
  )
}

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then

  const diffMins = Math.floor(diffMs / (1000 * 60))
  if (diffMins < 1) return 'just now'
  if (diffMins === 1) return '1 minute ago'
  if (diffMins < 60) return `${diffMins} minutes ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours === 1) return '1 hour ago'
  if (diffHours < 24) return `${diffHours} hours ago`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return '1 day ago'
  if (diffDays < 7) return `${diffDays} days ago`

  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks === 1) return '1 week ago'
  if (diffWeeks < 5) return `${diffWeeks} weeks ago`

  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths === 1) return '1 month ago'
  if (diffMonths < 12) return `${diffMonths} months ago`

  const diffYears = Math.floor(diffDays / 365)
  if (diffYears === 1) return '1 year ago'
  return `${diffYears} years ago`
}

export function getColumns(): ColumnDef<OrgUser>[] {
  return [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader label="Name" column={column} />,
      cell: ({ getValue }) => (
        <span className="font-medium">{getValue<string>()}</span>
      ),
      enableSorting: true,
    },
    {
      accessorKey: 'email',
      header: ({ column }) => <SortableHeader label="Email" column={column} />,
      cell: ({ getValue }) => (
        <span className="text-sm">{getValue<string>()}</span>
      ),
      enableSorting: true,
    },
    {
      accessorKey: 'role_display_name',
      header: 'Role',
      cell: ({ getValue }) => {
        const role = getValue<string | null>()
        return role ? (
          <Badge variant="outline">{role}</Badge>
        ) : (
          <span className="text-muted-foreground text-xs">No role</span>
        )
      },
      enableSorting: false,
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ getValue }) => {
        const active = getValue<boolean>()
        return (
          <Badge variant={active ? 'default' : 'secondary'}>
            {active ? 'Active' : 'Inactive'}
          </Badge>
        )
      },
      enableSorting: false,
    },
    {
      accessorKey: 'last_login',
      header: ({ column }) => <SortableHeader label="Last Login" column={column} />,
      cell: ({ getValue }) => {
        const value = getValue<string | null>()
        return (
          <span className="text-muted-foreground text-sm">
            {value ? timeAgo(value) : 'Never'}
          </span>
        )
      },
      enableSorting: true,
    },
  ]
}
